import React, { useState, useEffect, FocusEvent, FormEvent, ChangeEventHandler } from 'react';
import { connect } from 'react-redux';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { clearSearchInput, setZipCode } from '@apps/shared/src/providerSearch/actions';
import { Accessibility, SearchRadius } from '@apps/shared/src/providerSearch/types/providerSearch';
import IconButton from '@mui/material/IconButton';
import SubmitIcon from '@mui/icons-material/ArrowForward';
import { getProviders, validateZipCode } from '../actions';
import { RootState } from '../../store';
import { IncompleteTheme } from '../../shared/types/theme';

const Header = styled(Typography)(({ theme }) => ({
  ...(theme as IncompleteTheme).sidebar.headerText,
}));

const FormContainer = styled('form')({
  display: 'flex',
  alignItems: 'flex-start',
});

const StyledTextField = styled(TextField)({
  '& .MuiFormHelperText-root': {
    color: '#F57040 !important',
  },
});

type ParentProps = {
  zipcodeValue: string;
  setZipCodeValue: React.Dispatch<React.SetStateAction<string>>;
};

const mapStateToProps = ({ providerSearch, planSelect }: RootState): StateProps => ({
  selectedZipCode: providerSearch.selectedZipCode,
  searchInput: providerSearch.searchInput,
  isLoading: providerSearch.isLoadingProviderList,
  zipCodeLocation: providerSearch.zipCodeDetails,
  isZipUnrecognized: providerSearch.isZipUnrecognized,
  searchRadius: providerSearch.radius,
  selectedAccessibilities: providerSearch.selectedAccessibilities,
  selectedProviderType: providerSearch.selectedProviderType,
  providergendercode: providerSearch.providergendercode,
  entitytype: providerSearch.entitytype,
  selectedProviderSpecialty: providerSearch.selectedProviderSpecialty,
  planID: planSelect.selectedPlan?.id,
});

type StateProps = {
  selectedZipCode: string;
  searchInput: string;
  isLoading: boolean;
  isZipUnrecognized: boolean;
  selectedProviderType: string;
  zipCodeLocation: string;
  entitytype: string;
  providergendercode: string;
  selectedProviderSpecialty: string;
  searchRadius: SearchRadius;
  selectedAccessibilities: any[];
  planID: any;
};

const mapDispatchToProps = {
  setZipCode,
  getProviders,
  validateZipCode,
  clearSearchInput,
};

type DispatchProps = {
  setZipCode: typeof setZipCode;
  getProviders: (
    selectedZipCode: string,
    selecetedRadius: string,
    searchInput: string,
    limit: number,
    page: number,
    selectedAccessibilities: Accessibility[],
    selectedProviderType: string,
    selectedProviderSpecialty: string,
    providergendercode: string,
    entitytype: string
  ) => void;
  validateZipCode: (selectedZipCode: string) => void;
  clearSearchInput: () => void;
};

type Props = StateProps & DispatchProps & ParentProps & typeof mapDispatchToProps;

export function ZipCodeEntry({
  isLoading,
  selectedZipCode,
  searchInput,
  zipCodeLocation,
  planID,
  setZipCode,
  getProviders,
  validateZipCode,
  isZipUnrecognized,
  clearSearchInput,
  searchRadius,
  selectedAccessibilities,
  selectedProviderType,
  selectedProviderSpecialty,
  providergendercode,
  entitytype,
  zipcodeValue,
  setZipCodeValue,
}: Props): JSX.Element {
  const [zipCodeInput, setZipCodeInput] = useState(selectedZipCode);
  const [helperInfo, setHelperInfo] = useState({
    hasError: isZipUnrecognized,
    text: zipCodeLocation,
  });

  const isSubmitDisabled = isLoading || helperInfo.hasError;
  useEffect(() => {
    if (selectedZipCode && selectedZipCode.length === 5) validateZipCode(selectedZipCode);
  }, [selectedZipCode, validateZipCode]);

  useEffect(() => {
    if (selectedZipCode === '') {
      setZipCodeInput('');
      setHelperInfo({ hasError: false, text: '' });
    }
  }, [selectedZipCode]);

  useEffect(() => {
    setHelperInfo({ text: zipCodeLocation, hasError: isZipUnrecognized });
  }, [zipCodeLocation, isZipUnrecognized]);

  const applyZipCodeInputValidation = (value: string, isTyping: boolean): void => {
    if (value.length === 5) {
      setHelperInfo({ text: '', hasError: false });
      validateZipCode(value);
    } else if (value.length === 0 && !isTyping) {
      setHelperInfo({ text: 'Required', hasError: true });
    } else if (isTyping && value.length < 5) {
      setHelperInfo({ text: 'Must be 5 digits', hasError: true });
    } else if (!isTyping) {
      setHelperInfo({ text: 'Must be 5 digits', hasError: true });
    } else {
      setHelperInfo({ text: '', hasError: false });
    }
  };

  const handleZipCodeInput: ChangeEventHandler<HTMLInputElement> = e => {
    const value = e.currentTarget.value.replace(/\D/g, ''); // strip non-numeric
    applyZipCodeInputValidation(value, true);
    setZipCodeInput(value);
    setZipCodeValue(value);
    if (value.length === 5) {
      setZipCode(value);
      // getProviders(
      //   planID,
      //   value,
      //   searchRadius,
      //   searchInput,
      //   50,
      //   1,
      //   selectedAccessibilities,
      //   selectedProviderType,
      //   selectedProviderSpecialty,
      //   providergendercode,
      //   entitytype
      // );
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    clearSearchInput();

    if (!isSubmitDisabled) {
      applyZipCodeInputValidation(zipCodeInput, false);

      if (zipCodeInput.length === 5) {
        setZipCodeInput(zipCodeInput);
        setZipCodeValue(zipCodeInput); // Sync with parent component state
        setZipCode(zipCodeInput);
        // getProviders(
        //   planID,
        //   zipCodeInput,
        //   searchRadius,
        //   searchInput,
        //   50,
        //   1,
        //   selectedAccessibilities,
        //   selectedProviderType,
        //   selectedProviderSpecialty,
        //   providergendercode,
        //   entitytype
        // );
      }
    }
  };

  const handleFocus = (event: FocusEvent<HTMLInputElement>): void => {
    event.target.select();
  };

  return (
    <>
      <Header>Zip Code</Header>
      <FormContainer onSubmit={handleSubmit}>
        <StyledTextField
          id="zip-input"
          placeholder="Zip Code"
          aria-label="Zip Code Input"
          value={zipCodeInput}
          onChange={handleZipCodeInput}
          onFocus={handleFocus}
          fullWidth
          autoFocus // eslint-disable-line jsx-a11y/no-autofocus
          autoComplete="off"
          type="tel"
          disabled={isLoading}
          error={helperInfo.hasError}
          helperText={helperInfo.text}
          InputProps={{ inputProps: { maxLength: 5 } }}
        />
        {/* <IconButton
          color="primary"
          id="search-submit"
          edge="end"
          aria-label="submit zip-code"
          type="submit"
          disabled={isSubmitDisabled}
        >
          <SubmitIcon />
        </IconButton> */}
      </FormContainer>
    </>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(ZipCodeEntry);
